import React from 'react';
import { useTranslation } from 'react-i18next';

const COLORS = {
  bg: '#0a0a0f', surface: '#12121a', surface2: '#1a1a28', border: '#1e1e2e',
  text: '#e8e8ed', textDim: '#8888a0', textMuted: '#55556a',
  logos: '#6366F1', gold: '#F59E0B', viability: '#10B981', entropy: '#EF4444',
};
const fonts = { mono: "'JetBrains Mono', 'SF Mono', monospace", sans: "'Inter', sans-serif", display: "'Sora', sans-serif" };

const LS_KEY = 'logos-weekly-digest-date';

export function shouldShowDigest() {
  try {
    const lastDate = localStorage.getItem(LS_KEY) || '';
    if (!lastDate) return false; // Don't show on first ever session
    const daysSince = (Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24);
    return daysSince >= 7;
  } catch { return false; }
}

export function markDigestShown() {
  try { localStorage.setItem(LS_KEY, new Date().toISOString().slice(0, 10)); } catch {}
}

export function initDigestTracking() {
  try {
    if (!localStorage.getItem(LS_KEY)) {
      localStorage.setItem(LS_KEY, new Date().toISOString().slice(0, 10));
    }
  } catch {}
}

export default function WeeklyDigest({ lambda, prevWeekLambda, sessions, streak, strongestDomain, weakestDomain, onClose }) {
  const { t } = useTranslation();

  const deltaLambda = lambda - (prevWeekLambda || lambda);
  const deltaPercent = (deltaLambda * 100).toFixed(1);
  const isUp = deltaLambda > 0.005;
  const isDown = deltaLambda < -0.005;
  const deltaColor = isUp ? COLORS.viability : isDown ? COLORS.entropy : COLORS.textDim;
  const deltaIcon = isUp ? '📈' : isDown ? '📉' : '➡️';

  const handleClose = () => {
    markDigestShown();
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9996,
      background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeInUp 0.3s ease',
    }}>
      <div style={{
        width: '90%', maxWidth: 440,
        background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
        border: `1px solid ${COLORS.gold}25`, borderRadius: 18,
        padding: '24px 22px', position: 'relative',
        boxShadow: `0 0 80px ${COLORS.gold}10`,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 36, marginBottom: 6 }}>📊</div>
          <h3 style={{
            fontSize: 17, fontWeight: 700, margin: 0, fontFamily: fonts.display, color: COLORS.text,
            background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.logos})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {t('weeklyDigest.title', 'Resumen Semanal')}
          </h3>
        </div>

        {/* Delta Lambda */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          padding: '14px 16px', background: `${deltaColor}10`, border: `1px solid ${deltaColor}25`,
          borderRadius: 12, marginBottom: 14,
        }}>
          <span style={{ fontSize: 28 }}>{deltaIcon}</span>
          <div>
            <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1 }}>
              ΔΛ {t('weeklyDigest.thisWeek', 'esta semana')}
            </div>
            <div style={{ fontSize: 22, fontFamily: fonts.mono, color: deltaColor, fontWeight: 700 }}>
              {isUp ? '+' : ''}{deltaPercent}%
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div style={{ background: COLORS.surface2, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1, marginBottom: 4 }}>
              {t('weeklyDigest.sessions', 'SESIONES')}
            </div>
            <div style={{ fontSize: 20, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 700 }}>{sessions || 0}</div>
          </div>
          <div style={{ background: COLORS.surface2, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1, marginBottom: 4 }}>
              {t('misc.streakLabel', '🔥 STREAK')}
            </div>
            <div style={{ fontSize: 20, fontFamily: fonts.mono, color: COLORS.gold, fontWeight: 700 }}>{streak || 0}</div>
          </div>
          {strongestDomain && (
            <div style={{ background: `${COLORS.viability}08`, borderRadius: 10, padding: '10px 12px', border: `1px solid ${COLORS.viability}20` }}>
              <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.viability, letterSpacing: 1, marginBottom: 4 }}>
                {t('weeklyDigest.strongest', 'MÁS FUERTE')}
              </div>
              <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600 }}>{strongestDomain}</div>
            </div>
          )}
          {weakestDomain && (
            <div style={{ background: `${COLORS.entropy}08`, borderRadius: 10, padding: '10px 12px', border: `1px solid ${COLORS.entropy}20` }}>
              <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.entropy, letterSpacing: 1, marginBottom: 4 }}>
                {t('weeklyDigest.weakest', 'ATENCIÓN')}
              </div>
              <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600 }}>{weakestDomain}</div>
            </div>
          )}
        </div>

        {/* Motivational */}
        <div style={{
          fontSize: 11, fontFamily: fonts.sans, color: COLORS.textDim, textAlign: 'center',
          lineHeight: 1.5, marginBottom: 18, fontStyle: 'italic',
        }}>
          {isUp
            ? t('weeklyDigest.motivationUp', '¡Vas por buen camino! La consistencia es tu mayor aliado.')
            : isDown
            ? t('weeklyDigest.motivationDown', 'Cada día es una nueva oportunidad. Enfócate en tu dominio más débil.')
            : t('weeklyDigest.motivationStable', 'Estabilidad es buena señal. Busca pequeños saltos de calidad.')}
        </div>

        {/* Close button */}
        <button onClick={handleClose} style={{
          width: '100%', padding: '12px 20px', borderRadius: 10, cursor: 'pointer',
          background: `linear-gradient(135deg, ${COLORS.gold}90, ${COLORS.gold})`,
          border: 'none', color: '#000', fontFamily: fonts.mono, fontSize: 12,
          fontWeight: 700, letterSpacing: 1.5,
          boxShadow: `0 4px 20px ${COLORS.gold}30`,
        }}>
          {t('weeklyDigest.close', 'Continuar ✦')}
        </button>
      </div>
    </div>
  );
}
